import { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDialog } from "@/context/DialogProvider";
import { useSite } from "@/context/SiteContext";
import {
  Gauge, AlertTriangle, CheckCircle2, Building2,
  ChevronDown, ChevronRight, ShieldCheck, Archive,
} from "lucide-react";

/**
 * CCP - Data Capture
 *
 * Monthly capture grid + per-equipment expandable detail view + supervisor
 * sign-off ribbon + month archive lifecycle.
 *
 * Honours the master site filter:
 *   - specific site selected → one capture grid
 *   - "All Sites" → one separate table per site (with its own sign-off
 *     ribbon, archive button, etc.)
 *
 * Sign-off model (per (site × day)):
 *   - supervisor / manager / owner can sign off days for sites they have
 *     access to. The badge goes green; if a new reading lands after the
 *     sign-off, the badge turns amber ("stale — please re-sign").
 *
 * Archive model (per (site × month)):
 *   - only enabled once the month is over AND every day in the month is
 *     signed off. Archived months are hidden by default; toggle "Show
 *     archived" to bring them back.
 */
export default function CCPs() {
  const { notify, confirm } = useDialog();
  const { user } = useAuth();
  const { activeSiteId, isAllSites, sites } = useSite();
  const today = useMemo(() => new Date(), []);
  const defaultMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  const [month, setMonth] = useState(defaultMonth);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const canSignoff = ["owner", "manager", "supervisor"].includes(user?.role);

  // Archived months are now invisible on this page — preview / vault flows
  // run from the Reports page. Hide-by-default is the right behaviour for
  // the day-to-day sign-off UI; admins who need to inspect an archived
  // month can do so through the Reports → CCP Data Capture preview.
  const load = async (m) => {
    setBusy(true);
    try {
      const { data } = await api.get("/ccps/monitoring", {
        params: { month: m, include_archived: false },
      });
      setData(data);
    } catch (e) {
      await notify({ title: "Could not load capture data", message: formatErr(e.response?.data?.detail) || "Try again", variant: "err" });
    } finally { setBusy(false); }
  };
  useEffect(() => { load(month); /* eslint-disable-next-line */ }, [month, activeSiteId]);

  const totalMissing = data?.equipment?.reduce(
    (sum, e) => sum + e.days.filter((d) => d.is_missing).length, 0,
  ) ?? 0;
  const totalDeviations = data?.equipment?.reduce(
    (sum, e) => sum + e.days.reduce((s, d) => s + (d.deviations || 0), 0), 0,
  ) ?? 0;

  // Group equipment by site only when master filter is "All Sites".
  const equipmentGroups = useMemo(() => {
    if (!data?.equipment?.length) return [];
    if (!isAllSites) {
      return [{ site_id: activeSiteId, site_name: null, equipment: data.equipment }];
    }
    const byId = new Map(sites.map((s) => [s.id, s.name]));
    const buckets = new Map();
    for (const eq of data.equipment) {
      if (!buckets.has(eq.site_id)) buckets.set(eq.site_id, []);
      buckets.get(eq.site_id).push(eq);
    }
    return Array.from(buckets.entries())
      .map(([site_id, equipment]) => ({ site_id, site_name: byId.get(site_id) || "Unknown site", equipment }))
      .sort((a, b) => (a.site_name || "").localeCompare(b.site_name || ""));
  }, [data, isAllSites, activeSiteId, sites]);

  // ---- Sign-off / archive actions (passed down to each table) ----
  const signoffDays = async (siteId, dates) => {
    try {
      const { data: res } = await api.post("/ccps/signoff", { site_id: siteId, dates });
      await load(month);
      // Backend silently SKIPS days that fail the eligibility check
      // (missing readings / open deviations / future date). Surface that
      // so the supervisor isn't left wondering why the cell didn't move.
      const skipped = res?.skipped || [];
      const signed = res?.signed ?? 0;
      if (skipped.length && signed === 0) {
        const lines = skipped.slice(0, 5).map((s) => `• ${s.date}: ${s.reason || "not eligible"}`);
        if (skipped.length > 5) lines.push(`…and ${skipped.length - 5} more`);
        await notify({
          title: "Sign-off skipped",
          message: lines.join("\n"),
          variant: "warn",
        });
      } else if (skipped.length) {
        await notify({
          title: `Signed ${signed} day${signed === 1 ? "" : "s"} · ${skipped.length} skipped`,
          message: skipped.slice(0, 3).map((s) => `• ${s.date}: ${s.reason || "not eligible"}`).join("\n"),
          variant: "warn",
        });
      }
    } catch (e) {
      await notify({ title: "Could not sign off", message: formatErr(e.response?.data?.detail) || "Try again", variant: "err" });
    }
  };
  const unsignoffDays = async (siteId, dates) => {
    const ok = await confirm({
      title: `Remove sign-off${dates.length > 1 ? "s" : ""}?`,
      message: `${dates.length} day${dates.length > 1 ? "s" : ""} will return to unsigned state.`,
    });
    if (!ok) return;
    try {
      await api.delete("/ccps/signoff", { data: { site_id: siteId, dates } });
      await load(month);
    } catch (e) {
      await notify({ title: "Could not remove sign-off", message: formatErr(e.response?.data?.detail) || "Try again", variant: "err" });
    }
  };
  const archiveMonth = async (siteId, siteName) => {
    const ok = await confirm({
      title: `Archive ${month}?`,
      message: `${siteName} · ${month} will be hidden from the default view. You can restore it any time via "Show archived".`,
    });
    if (!ok) return;
    try {
      await api.post("/ccps/archive", { site_id: siteId, month });
      await load(month);
    } catch (e) {
      await notify({ title: "Could not archive", message: formatErr(e.response?.data?.detail) || "Try again", variant: "err" });
    }
  };
  const unarchiveMonth = async (siteId, siteName) => {
    const ok = await confirm({
      title: `Restore ${month}?`,
      message: `${siteName} · ${month} will be visible in the default view again.`,
    });
    if (!ok) return;
    try {
      await api.delete("/ccps/archive", { data: { site_id: siteId, month } });
      await load(month);
    } catch (e) {
      await notify({ title: "Could not restore", message: formatErr(e.response?.data?.detail) || "Try again", variant: "err" });
    }
  };

  return (
    <div className="space-y-5" data-testid="ccp-monitoring-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="vb-heading text-3xl m-0 flex items-center gap-2" style={{ color: "var(--vb-text-2)" }}>
            <Gauge size={24} /> CCP - Data Capture
          </h1>
          <p className="text-sm opacity-70 m-0">
            Daily readings per equipment. Yellow tiles flag missing days, red marks deviations.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input type="month" className="vb-input" value={month}
            max={defaultMonth}
            onChange={(e) => setMonth(e.target.value)}
            data-testid="ccp-month-picker" />
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="vb-card p-4" data-testid="ccp-tile-equipment">
          <div className="text-xs opacity-60 mb-1">Equipment monitored</div>
          <div className="vb-heading text-2xl m-0" style={{ color: "var(--vb-text-2)" }}>{data?.equipment?.length ?? "—"}</div>
        </div>
        <div className="vb-card p-4" data-testid="ccp-tile-missing"
          style={{ background: totalMissing > 0 ? "var(--vb-warning-bg, #fef3c7)" : undefined }}>
          <div className="text-xs opacity-60 mb-1 flex items-center gap-1.5">
            <AlertTriangle size={12} style={{ color: "var(--vb-warning)" }} /> Days with missing capture
          </div>
          <div className="vb-heading text-2xl m-0" style={{ color: totalMissing > 0 ? "var(--vb-warning)" : "var(--vb-text-2)" }}>
            {totalMissing}
          </div>
        </div>
        <div className="vb-card p-4" data-testid="ccp-tile-deviations"
          style={{ background: totalDeviations > 0 ? "#fee2e2" : undefined }}>
          <div className="text-xs opacity-60 mb-1 flex items-center gap-1.5">
            <AlertTriangle size={12} style={{ color: "var(--vb-error)" }} /> Deviations this month
          </div>
          <div className="vb-heading text-2xl m-0" style={{ color: totalDeviations > 0 ? "var(--vb-error)" : "var(--vb-text-2)" }}>
            {totalDeviations}
          </div>
        </div>
      </div>

      {/* Capture grid(s) */}
      {!data ? (
        <div className="vb-card p-6 text-center text-sm opacity-70" data-testid="ccp-loading">Loading…</div>
      ) : data.equipment.length === 0 ? (
        <div className="vb-card p-6 text-sm opacity-70" data-testid="ccp-empty">
          No equipment found for this month. Try picking another month.
        </div>
      ) : (
        equipmentGroups.map((group) => (
          <CaptureTable
            key={group.site_id || "single"}
            group={group}
            month={month}
            daysInMonth={data.days_in_month}
            showSiteHeader={isAllSites}
            signoffs={data.signoffs || {}}
            eligibility={data.eligibility || {}}
            archives={data.archives || []}
            closureMapBySite={data.closure_map_by_site || {}}
            canSignoff={canSignoff}
            onSignoff={signoffDays}
            onUnsignoff={unsignoffDays}
            onArchive={archiveMonth}
            onUnarchive={unarchiveMonth}
          />
        ))
      )}
    </div>
  );
}

function CaptureTable({
  group, month, daysInMonth, showSiteHeader,
  signoffs, eligibility, archives, closureMapBySite, canSignoff,
  onSignoff, onUnsignoff, onArchive, onUnarchive,
}) {
  const { notify } = useDialog();
  const [expanded, setExpanded] = useState(() => new Set());
  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // Day-level helpers (the sign-off ribbon row).
  const today = new Date();
  const isFutureDay = (d) => {
    const dt = new Date(`${month}-${String(d).padStart(2, "0")}T00:00:00`);
    return dt > today;
  };
  const dateStr = (d) => `${month}-${String(d).padStart(2, "0")}`;
  const signoffFor = (d) => signoffs[`${group.site_id}:${dateStr(d)}`] || null;
  const eligibilityFor = (d) => eligibility[`${group.site_id}:${dateStr(d)}`] || null;
  const siteClosures = closureMapBySite[group.site_id] || {};
  const closureFor = (d) => siteClosures[dateStr(d)] || null;

  // Archive status for this site/month.
  const archive = archives.find((a) => a.site_id === group.site_id) || null;

  // Sign-off completion: required = past + today days that are NOT closed.
  const requiredDays = [];
  for (let d = 1; d <= daysInMonth; d++) {
    if (!isFutureDay(d) && !closureFor(d)) requiredDays.push(d);
  }
  const signedDays = requiredDays.filter((d) => signoffFor(d));
  const staleDays = requiredDays.filter((d) => signoffFor(d)?.stale);
  // Eligible-to-sign = unsigned OR stale, AND day passes the eligibility
  // rule (every equipment complete + every deviation closed). Blocked =
  // unsigned but ineligible. Drives the batch button label.
  const eligibleToSign = requiredDays.filter((d) => {
    const so = signoffFor(d);
    const elig = eligibilityFor(d);
    return (!so || so.stale) && elig?.eligible;
  });
  const blockedDays = requiredDays.filter((d) => {
    const so = signoffFor(d);
    const elig = eligibilityFor(d);
    return !so && elig && !elig.eligible;
  });
  // Archive only when month is fully over (no future days in this month).
  // Compare against the number of non-future days (not "required", since
  // closed days are excluded from the requirement set).
  let nonFutureCount = 0;
  for (let d = 1; d <= daysInMonth; d++) if (!isFutureDay(d)) nonFutureCount += 1;
  const monthIsOver = nonFutureCount === daysInMonth;
  const fullySignedMonth = monthIsOver
    && signedDays.length === requiredDays.length
    && staleDays.length === 0;

  const batchSignAll = () => {
    if (!eligibleToSign.length) return;
    onSignoff(group.site_id, eligibleToSign.map(dateStr));
  };

  const onDayCellClick = (d) => {
    if (isFutureDay(d) || !canSignoff) return;
    const so = signoffFor(d);
    if (so && !so.stale) {
      // Already signed — clicking un-signs.
      onUnsignoff(group.site_id, [dateStr(d)]);
      return;
    }
    const elig = eligibilityFor(d);
    if (elig && !elig.eligible) {
      // Block with an explanatory toast so the supervisor knows what to fix.
      notify({
        title: `Day ${d} can't be signed yet`,
        message: elig.reason || "Missing readings or unresolved deviations.",
        variant: "warn",
      });
      return;
    }
    onSignoff(group.site_id, [dateStr(d)]);
  };

  return (
    <div className="vb-card p-4 overflow-x-auto" data-testid={`ccp-grid-${group.site_id || "single"}`}>
      {(showSiteHeader || archive) && (
        <div className="flex items-center gap-2 mb-3 pb-2 flex-wrap" style={{ borderBottom: "1px solid var(--vb-border)" }}>
          {showSiteHeader && <Building2 size={16} style={{ color: "var(--vb-text-2)" }} />}
          {showSiteHeader && (
            <h3 className="vb-heading text-base m-0" style={{ color: "var(--vb-text-2)" }}
              data-testid={`ccp-site-heading-${group.site_id}`}>
              {group.site_name}
            </h3>
          )}
          {showSiteHeader && <span className="text-xs opacity-60 ml-1">· {group.equipment.length} equipment</span>}
          {archive && (
            <span className="vb-pill warn ml-auto" data-testid={`ccp-archived-badge-${group.site_id}`}>
              <Archive size={12} /> Archived by {archive.archived_by_name}
            </span>
          )}
        </div>
      )}

      <table className="text-sm" style={{ borderCollapse: "collapse", whiteSpace: "nowrap" }}>
        <thead>
          <tr style={{ borderBottom: "1px solid var(--vb-border)" }}>
            <th className="text-left pr-3 pb-2 md:sticky md:left-0" style={{ background: "var(--vb-card)", zIndex: 2, width: 24 }}></th>
            <th className="text-left pr-3 pb-2 md:sticky md:left-6" style={{ background: "var(--vb-card)", zIndex: 2 }}>Equipment</th>
            <th className="text-left pr-3 pb-2">Range</th>
            <th className="text-center pr-3 pb-2">Freq</th>
            {Array.from({ length: daysInMonth }, (_, i) => (
              <th key={i} className="text-center pb-2 pr-0.5" style={{ width: 26, fontWeight: 500 }}>{i + 1}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {group.equipment.map((eq) => {
            const isOpen = expanded.has(eq.equipment_id);
            return [
              <tr key={`row-${eq.equipment_id}`} data-testid={`ccp-row-${eq.equipment_id}`}
                style={{ borderBottom: "1px solid var(--vb-border)", cursor: "pointer" }}
                onClick={() => toggle(eq.equipment_id)}>
                <td className="py-1.5 pr-1 md:sticky md:left-0 text-center" style={{ background: "var(--vb-card)", zIndex: 1 }}
                  data-testid={`ccp-row-toggle-${eq.equipment_id}`}>
                  {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </td>
                <td className="py-1.5 pr-3 md:sticky md:left-6" style={{ background: "var(--vb-card)", zIndex: 1 }}>
                  <strong>{eq.equipment_name}</strong>
                  {eq.monitoring_mode === "exception" && (
                    <span className="vb-pill warn ml-2 text-[9px]" data-testid={`ccp-exception-badge-${eq.equipment_id}`}
                      style={{ padding: "1px 6px" }} title="Monitored by an external IoT logger — deviations only">
                      Exception
                    </span>
                  )}
                  {eq.type_name && <div className="text-[11px] opacity-60">{eq.type_name}</div>}
                </td>
                <td className="py-1.5 pr-3 text-[11px] opacity-80">
                  {eq.min_value !== null && eq.min_value !== undefined ? `${eq.min_value}–${eq.max_value}${eq.unit}` : "—"}
                </td>
                <td className="py-1.5 pr-3 text-center font-mono text-[11px]">
                  {eq.monitoring_mode === "exception" ? "on alarm" : `${eq.frequency}/day`}
                </td>
                {eq.days.map((d) => {
                  const bg = d.is_closed ? "#e5e7eb"
                    : d.is_future ? "transparent"
                    : d.deviations > 0 ? "#fee2e2"
                    : d.is_missing ? "#fef3c7"
                    : d.count === 0 ? "transparent"
                    : "#dcfce7";
                  const fg = d.is_closed ? "#6b7280"
                    : d.is_future ? "var(--vb-text-1)"
                    : d.deviations > 0 ? "var(--vb-error)"
                    : d.is_missing ? "var(--vb-warning)"
                    : "var(--vb-accent)";
                  const label = d.is_closed ? (d.closure_type || "CL")
                    : d.is_future ? ""
                    : d.deviations > 0 ? "!"
                    : d.is_missing ? `${d.count}`
                    : d.count === 0 ? ""
                    : "✓";
                  const title = d.is_closed
                    ? `Closed — ${d.closure_type === "PH" ? "Public Holiday" : "Closed"}${d.closure_reason ? ` · ${d.closure_reason}` : ""}`
                    : d.is_future ? "Future"
                    : `${d.count} reading(s)${d.deviations ? ` · ${d.deviations} deviation(s)` : ""}`;
                  return (
                    <td key={d.day} className="text-center"
                      title={title}
                      data-testid={`ccp-cell-${eq.equipment_id}-${d.day}`}
                      style={{
                        width: 26, height: 26,
                        background: bg, color: fg,
                        border: "1px solid var(--vb-border)",
                        fontWeight: d.is_closed ? 700 : 600,
                        fontSize: d.is_closed ? 9 : 11,
                      }}>
                      {label}
                    </td>
                  );
                })}
              </tr>,
              isOpen && (
                <tr key={`detail-${eq.equipment_id}`} data-testid={`ccp-detail-${eq.equipment_id}`}
                  style={{ borderBottom: "1px solid var(--vb-border)" }}>
                  <td colSpan={4 + daysInMonth} style={{ background: "var(--vb-bg-2, #faf6f1)", padding: "10px 16px" }}>
                    <EquipmentDetail eq={eq} />
                  </td>
                </tr>
              ),
            ];
          })}

          {/* Sign-off ribbon row */}
          <tr data-testid={`ccp-signoff-row-${group.site_id || "single"}`}
            style={{ borderTop: "2px solid var(--vb-border)", background: "var(--vb-bg-2, #faf6f1)" }}>
            <td colSpan={4} className="py-2 pr-3 md:sticky md:left-0 text-[11px] font-medium"
              style={{ background: "var(--vb-bg-2, #faf6f1)", zIndex: 1 }}>
              <div className="flex items-center gap-1.5">
                <ShieldCheck size={13} style={{ color: "var(--vb-accent)" }} />
                Sign-off
              </div>
            </td>
            {Array.from({ length: daysInMonth }, (_, i) => {
              const d = i + 1;
              const future = isFutureDay(d);
              const closure = closureFor(d);
              const isClosed = !!closure;
              const so = signoffFor(d);
              const isStale = so?.stale;
              const elig = eligibilityFor(d);
              const blocked = !future && !isClosed && !so && elig && !elig.eligible;
              const bg = isClosed
                ? "#e5e7eb"
                : future
                ? "transparent"
                : so
                ? (isStale ? "#fef3c7" : "#dcfce7")
                : blocked
                ? "#fee2e2"
                : "transparent";
              const fg = isClosed
                ? "#6b7280"
                : future
                ? "transparent"
                : so
                ? (isStale ? "var(--vb-warning)" : "var(--vb-accent)")
                : blocked
                ? "var(--vb-error)"
                : "var(--vb-text-1)";
              const label = isClosed
                ? (closure.type || "CL")
                : future
                ? ""
                : so
                ? (isStale ? "!" : "✓")
                : blocked
                ? "✕"
                : "○";
              const title = isClosed
                ? `Closed — ${closure.type === "PH" ? "Public Holiday" : "Closed"}${closure.reason ? ` · ${closure.reason}` : ""}`
                : future
                ? "Future"
                : so
                ? `Signed by ${so.signed_by_name} at ${(so.signed_at || "").slice(0, 16).replace("T", " ")}${isStale ? " · STALE — re-sign needed" : ""}`
                : blocked
                ? `Blocked — ${elig?.reason || "incomplete data"}`
                : "Eligible — click to sign off";
              return (
                <td key={d} className="text-center"
                  title={title}
                  data-testid={`ccp-signoff-cell-${group.site_id}-${d}`}
                  style={{
                    width: 26, height: 26,
                    background: bg, color: fg,
                    border: future ? "1px solid transparent" : "1px solid var(--vb-border)",
                    fontWeight: 700, fontSize: isClosed ? 9 : 11,
                    cursor: (future || isClosed || !canSignoff) ? "default" : "pointer",
                  }}
                  onClick={() => { if (!isClosed) onDayCellClick(d); }}>
                  {label}
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>

      {/* Footer ribbon: status + bulk actions */}
      <div className="flex gap-3 mt-3 flex-wrap items-center text-[12px]">
        <span className="vb-pill" data-testid={`ccp-signoff-progress-${group.site_id}`}>
          <ShieldCheck size={12} /> {signedDays.length - staleDays.length}/{requiredDays.length} days signed
          {staleDays.length > 0 && <span className="opacity-80">· {staleDays.length} stale</span>}
        </span>
        {blockedDays.length > 0 && (
          <span className="vb-pill warn" data-testid={`ccp-signoff-blocked-${group.site_id}`}
            title="These days have missing readings or unresolved deviations — fix the data before signing.">
            <AlertTriangle size={12} /> {blockedDays.length} blocked
          </span>
        )}
        {canSignoff && !archive && eligibleToSign.length > 0 && (
          <button className="vb-btn sm" onClick={batchSignAll}
            data-testid={`ccp-signoff-batch-${group.site_id}`}>
            <ShieldCheck size={12} /> Sign off {eligibleToSign.length} eligible day{eligibleToSign.length > 1 ? "s" : ""}
          </button>
        )}
        {canSignoff && !archive && fullySignedMonth && (
          <button className="vb-btn sm ghost" onClick={() => onArchive(group.site_id, group.site_name || "this site")}
            data-testid={`ccp-archive-btn-${group.site_id}`}>
            <Archive size={12} /> Archive {month}
          </button>
        )}
        {canSignoff && archive && (
          <button className="vb-btn sm ghost" onClick={() => onUnarchive(group.site_id, group.site_name || "this site")}
            data-testid={`ccp-unarchive-btn-${group.site_id}`}>
            <Archive size={12} /> Restore from archive
          </button>
        )}
        <span className="opacity-60 ml-auto">
          <span style={{ background: "#dcfce7", padding: "2px 6px", borderRadius: 2 }}>✓</span>&nbsp;Signed ·{" "}
          <span style={{ background: "#fee2e2", padding: "2px 6px", borderRadius: 2 }}>✕</span>&nbsp;Blocked ·{" "}
          <span style={{ padding: "2px 6px", borderRadius: 2, border: "1px solid var(--vb-border)" }}>○</span>&nbsp;Eligible
        </span>
      </div>
      <div className="mt-2 flex items-center gap-1 text-[11px] opacity-60">
        <CheckCircle2 size={11} style={{ color: "var(--vb-accent)" }} /> Tip: click any equipment row to expand the recorded readings.
      </div>
    </div>
  );
}

/** Expandable per-equipment detail — one row per day in the displayed
 * month showing each recorded value for that day (deviations in red).
 * Auditable context (who logged it, notes, full timestamp) lives in the
 * PDF report — keep this view scan-friendly. */
function EquipmentDetail({ eq }) {
  // Only show days that actually had readings, so the panel stays compact.
  const daysWithReadings = useMemo(
    () => eq.days.filter((d) => (d.readings || []).length > 0),
    [eq],
  );
  if (!daysWithReadings.length) {
    return (
      <div className="text-[12px] opacity-70" data-testid={`ccp-detail-empty-${eq.equipment_id}`}>
        No readings recorded for <strong>{eq.equipment_name}</strong> this month.
      </div>
    );
  }
  // Column count = max readings on any single day (so we have enough
  // value slots without wasting space when most days only have 1-2).
  const maxReadings = Math.max(eq.frequency, ...daysWithReadings.map((d) => d.readings.length));

  return (
    <div className="text-[12px]" data-testid={`ccp-detail-list-${eq.equipment_id}`}>
      <div className="flex items-center gap-2 mb-2">
        <strong>{eq.equipment_name}</strong>
        <span className="opacity-60">
          · {daysWithReadings.length} day{daysWithReadings.length > 1 ? "s" : ""} with {eq.monitoring_mode === "exception" ? "deviations" : "readings"} ·
          {eq.monitoring_mode === "exception" ? " deviations only (external logger)" : ` target ${eq.frequency}/day`}
        </span>
      </div>
      <div style={{ maxHeight: 280, overflowY: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--vb-border)", textAlign: "left" }}>
              <th className="py-1 pr-3" style={{ width: 60 }}>Day</th>
              {Array.from({ length: maxReadings }, (_, i) => (
                <th key={i} className="py-1 pr-3">Reading {i + 1}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {daysWithReadings.map((d) => (
              <tr key={d.day} data-testid={`ccp-detail-day-${eq.equipment_id}-${d.day}`}
                style={{ borderBottom: "1px solid var(--vb-border)" }}>
                <td className="py-1 pr-3 font-mono">{d.day}</td>
                {Array.from({ length: maxReadings }, (_, i) => {
                  const r = d.readings[i];
                  if (!r) return <td key={i} className="py-1 pr-3 opacity-30">—</td>;
                  const isDev = r.is_deviation;
                  return (
                    <td key={i} className="py-1 pr-3 font-mono"
                      style={{ color: isDev ? "var(--vb-error)" : "inherit", fontWeight: isDev ? 700 : 500 }}
                      title={isDev ? "Deviation" : "In range"}>
                      {r.value}{r.unit || eq.unit || ""}
                      {isDev && <span style={{ marginLeft: 4 }}>!</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
